import { FC, memo, useEffect, useState, useCallback, ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import { useForm, SubmitHandler, UseFormSetValue } from 'react-hook-form';
import styled from 'styled-components';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { AUTH_ROUTES } from '@navigation/types';
import theme from 'theme';
import { isErrorWithStringMessage, ErrorType } from '@redux/baseQuery';
import { setForgotPasswordEmail } from '@redux/state/authSlice';
import { useAppDispatch } from '@redux/store';
import { useForgotPasswordMutation } from '@redux/queries/auth';
import { email } from '@utils/constants/schemas';
import {
  TEXT_INPUT_PLACEHOLDERS,
  TEXT_INPUT_LABELS,
  TEXT_INPUT_ICONS,
} from '@utils/constants/form';
import AuthLayout from '@components/layouts/AuthLayout';
import AuthTitle from '@components/auth/AuthTitle';
import TextInput from '@components/fields/input';
import {
  StyledForm,
  StyledButton,
  StyledErrorContainer,
  StyledErrorText,
} from '@components/form/styled';

const Content = styled.div`
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 24px;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

type ForgotPasswordState = {
  email: string;
};

const defaultValues: ForgotPasswordState = {
  email: '',
};

const schema = yup.object().shape({
  email,
});

const DEFAULT_ERROR_MESSAGE = 'Invalid data';

const ForgotPassword: FC = () => {
  const [forgotPassword, { isSuccess, isLoading, isError, error }] =
    useForgotPasswordMutation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    reset: resetForm,
    watch,
    formState: { errors },
  } = useForm<ForgotPasswordState>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleChange = useCallback(
    ({
      field,
      text,
      onChange,
    }: {
      field: keyof ForgotPasswordState;
      text: string;
      onChange: UseFormSetValue<ForgotPasswordState>;
    }) => {
      clearErrors(field);
      onChange(field, text);

      if (errorMessage) {
        setErrorMessage('');
      }
    },
    [clearErrors, errorMessage, setErrorMessage],
  );

  const onSubmit: SubmitHandler<ForgotPasswordState> = (
    data: ForgotPasswordState,
  ) => {
    if (data) {
      forgotPassword(data);
    }

    resetForm({}, { keepValues: true, keepDirty: false });
  };

  useEffect(() => {
    if (isSuccess) {
      dispatch(setForgotPasswordEmail(watch('email')));
      navigate(AUTH_ROUTES.SET_PASSWORD, {
        replace: true,
      });
    }
  }, [isSuccess, navigate, watch, dispatch]);

  useEffect(() => {
    if (isError) {
      setErrorMessage(
        isErrorWithStringMessage(error)
          ? (error as ErrorType)?.data?.message || DEFAULT_ERROR_MESSAGE
          : DEFAULT_ERROR_MESSAGE,
      );
    }
  }, [isError, error]);

  return (
    <AuthLayout>
      <Content>
        <AuthTitle
          title='Forgot Password'
          subtitle={
            "Enter your email address below.\nWe'll look for your account and send you\na password reset email."
          }
        />
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <FormContainer>
            <TextInput
              placeholder={TEXT_INPUT_PLACEHOLDERS.EMAIL}
              label={TEXT_INPUT_LABELS.EMAIL}
              icon={TEXT_INPUT_ICONS.EMAIL}
              errorText={errors.email?.message}
              {...register('email')}
              value={watch('email')}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange({
                  field: 'email',
                  text: e.target.value,
                  onChange: setValue,
                })
              }
            />

            <StyledErrorContainer>
              {errorMessage && (
                <StyledErrorText>{errorMessage}</StyledErrorText>
              )}
            </StyledErrorContainer>

            <StyledButton
              disabled={isLoading || !watch('email')}
              isLoading={isLoading}
              type='submit'
              textStyle={{ fontSize: theme.fonts.fontSize.xl }}
            >
              Reset Password
            </StyledButton>
          </FormContainer>
        </StyledForm>
      </Content>
    </AuthLayout>
  );
};

export default memo(ForgotPassword);
